/**
 * El aviso del descanso con la app CERRADA (Fase 6, 0.15.0).
 *
 * Es la segunda mitad de lo que empezó en la 0.12.0. Ahí quedó claro lo que
 * un iPhone deja hacer: mientras la app sigue despierta, el audio hace sonar
 * los tonos y la notificación pone el banner; en cuanto la app se cierra, no
 * corre nada. Lo único que atraviesa eso es Web Push, y Web Push necesita que
 * alguien mande el aviso desde fuera en el instante exacto.
 *
 * Ese alguien es `servidor/avisos/`, un Cloudflare Worker del plan gratuito.
 * Del teléfono salen **dos cosas y nada más**: la suscripción que da Apple y
 * el instante del aviso. Ni comidas, ni pesajes, ni medidas, ni historial, ni
 * el nombre. Y el servidor borra el registro al entregarlo.
 *
 * Qué pide el iPhone para que esto funcione (Req. 2.9):
 *
 * - La app **instalada en la pantalla de inicio**. En Safari normal no existe
 *   `PushManager`, por decisión de Apple.
 * - El permiso de avisos concedido, pedido dentro de un toque.
 *
 * Si falta cualquiera de las dos, la pantalla lo dice en lugar de prometer un
 * aviso que no va a llegar.
 */

import { permisoDeAvisos, pedirPermisoDeAvisos, type EstadoPermiso } from './avisos';

/**
 * Dirección del servidor y su clave pública. No son secretas: la clave
 * pública identifica al servidor y viaja en cada aviso, y la dirección está
 * en el código de una app que se sirve abierta. Van aquí y no en una variable
 * de entorno porque Cloudflare Pages compila desde el repositorio sin
 * configuración extra, y un valor que falta dejaría la función muerta sin
 * decir por qué.
 *
 * La privada vive solo como secreto del Worker y nunca estuvo aquí.
 */
export const SERVIDOR_AVISOS = 'https://duofit-avisos.yaneth-ruiz.workers.dev';
export const CLAVE_PUBLICA_AVISOS =
  'BKwVrKoqYYa5EZyt1gpMlr4XpaSgEa4LPcEqV9tbIFrr6pkAC1sfQyQIW-35RZW6TjANKwY9yQA3vYlg5QMVvco';

/** Lo más pronto que el servidor acepta un aviso, y lo más tarde. */
const MINIMO_MS = 3_000;
const MAXIMO_MS = 2 * 60 * 60 * 1000;

const TIEMPO_LIMITE_MS = 8_000;

// ---------------------------------------------------------------------------
// Lo que se manda, armado aparte para poder probarlo
// ---------------------------------------------------------------------------

export interface CuerpoDeProgramacion {
  suscripcion: { endpoint: string; p256dh: string; auth: string };
  enMs: number;
}

/**
 * El cuerpo de "programar un aviso", o null si no hay nada que programar.
 *
 * Se arma campo por campo a propósito: la suscripción del navegador trae más
 * cosas (`expirationTime`, y lo que agregue el navegador en el futuro) y de
 * aquí solo sale lo que el servidor necesita. Es la misma regla que aplica el
 * servidor del otro lado, por si alguna de las dos se equivoca.
 */
export function cuerpoDeProgramacion(
  suscripcion: PushSubscriptionJSON,
  terminaEn: string | null,
  ahoraMs: number,
): CuerpoDeProgramacion | null {
  if (terminaEn === null) return null;
  const enMs = Date.parse(terminaEn);
  if (Number.isNaN(enMs)) return null;
  const falta = enMs - ahoraMs;
  // Un descanso que ya terminó, o uno absurdamente largo, no se programa: el
  // servidor lo rechazaría y no hay nada que avisar
  if (falta < MINIMO_MS || falta > MAXIMO_MS) return null;

  const endpoint = suscripcion.endpoint;
  const p256dh = suscripcion.keys?.['p256dh'];
  const auth = suscripcion.keys?.['auth'];
  if (typeof endpoint !== 'string' || typeof p256dh !== 'string' || typeof auth !== 'string') {
    return null;
  }
  return { suscripcion: { endpoint, p256dh, auth }, enMs };
}

// ---------------------------------------------------------------------------
// Soporte y suscripción
// ---------------------------------------------------------------------------

export type EstadoAvisoCerrado =
  /** Este navegador no tiene Web Push. En iPhone: falta instalar la app. */
  | 'sin_soporte'
  /** Se puede, pero falta el permiso de avisos. */
  | 'sin_permiso'
  /** El permiso está negado: se cambia en los ajustes del iPhone. */
  | 'negado'
  /** Todo listo pero apagado. */
  | 'apagado'
  /** Encendido y suscrito. */
  | 'encendido';

export function avisosConAppCerradaSoportados(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function registro(): Promise<ServiceWorkerRegistration | null> {
  if (!avisosConAppCerradaSoportados()) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/** La suscripción de este teléfono, o null si no está suscrito. */
export async function suscripcionActual(): Promise<PushSubscription | null> {
  const r = await registro();
  if (r === null) return null;
  try {
    return await r.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** En qué estado está el aviso con la app cerrada, para la pantalla. */
export async function estadoAvisoCerrado(encendido: boolean): Promise<EstadoAvisoCerrado> {
  if (!avisosConAppCerradaSoportados()) return 'sin_soporte';
  const permiso = permisoDeAvisos();
  if (permiso === 'negado') return 'negado';
  if (permiso !== 'concedido') return 'sin_permiso';
  if (!encendido) return 'apagado';
  return (await suscripcionActual()) === null ? 'apagado' : 'encendido';
}

function bytesDeLaClave(base64url: string): Uint8Array<ArrayBuffer> {
  const normal = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const relleno = normal.length % 4 === 0 ? '' : '='.repeat(4 - (normal.length % 4));
  const binario = atob(normal + relleno);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

/**
 * Pide el permiso si falta y se suscribe. Hay que llamarlo dentro de un
 * toque. Devuelve el estado que quedó, para decirlo tal cual en la pantalla.
 */
export async function activarAvisoConAppCerrada(): Promise<EstadoAvisoCerrado> {
  if (!avisosConAppCerradaSoportados()) return 'sin_soporte';
  let permiso: EstadoPermiso = permisoDeAvisos();
  if (permiso !== 'concedido') permiso = await pedirPermisoDeAvisos();
  if (permiso === 'negado') return 'negado';
  if (permiso !== 'concedido') return 'sin_permiso';

  const r = await registro();
  if (r === null) return 'sin_soporte';
  try {
    const ya = await r.pushManager.getSubscription();
    if (ya !== null) return 'encendido';
    await r.pushManager.subscribe({
      // Apple lo exige: cada aviso tiene que mostrar algo al usuario
      userVisibleOnly: true,
      applicationServerKey: bytesDeLaClave(CLAVE_PUBLICA_AVISOS),
    });
    return 'encendido';
  } catch {
    return 'apagado';
  }
}

/**
 * Apaga el aviso: cancela lo que hubiera pendiente en el servidor y cancela
 * la suscripción. En ese orden, para no dejar un aviso programado contra una
 * suscripción que ya no existe.
 */
export async function desactivarAvisoConAppCerrada(): Promise<void> {
  const suscripcion = await suscripcionActual();
  if (suscripcion === null) return;
  await cancelarEnServidor(suscripcion.endpoint);
  try {
    await suscripcion.unsubscribe();
  } catch {
    // Si el navegador no la suelta, el aviso ya quedó cancelado del lado del servidor
  }
}

// ---------------------------------------------------------------------------
// Hablar con el servidor
// ---------------------------------------------------------------------------

async function pedir(ruta: string, cuerpo: unknown): Promise<boolean> {
  try {
    const respuesta = await fetch(`${SERVIDOR_AVISOS}${ruta}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cuerpo),
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    });
    return respuesta.ok;
  } catch {
    // Sin internet no hay aviso con la app cerrada, y no es un error que
    // valga interrumpir el entrenamiento: el sonido sigue funcionando
    return false;
  }
}

async function cancelarEnServidor(endpoint: string): Promise<boolean> {
  return pedir('/cancelar', { suscripcion: { endpoint } });
}

/**
 * Pone al día el aviso del servidor con el descanso que corre ahora.
 *
 * Se llama en el mismo lugar que el aviso de siempre: al empezar un descanso,
 * al cambiarlo y al terminarlo. Con `terminaEn` en null cancela, que es lo que
 * pasa al saltar el descanso o al terminar el entrenamiento.
 *
 * No lanza nunca. Un servidor caído o sin internet no puede dejar a medias un
 * entrenamiento: el sonido y el banner siguen siendo lo principal.
 */
export async function sincronizarAvisoDelServidor(terminaEn: string | null): Promise<void> {
  const suscripcion = await suscripcionActual();
  if (suscripcion === null) return;

  const cuerpo = cuerpoDeProgramacion(suscripcion.toJSON(), terminaEn, Date.now());
  if (cuerpo === null) {
    await cancelarEnServidor(suscripcion.endpoint);
    return;
  }
  await pedir('/programar', cuerpo);
}

// ---------------------------------------------------------------------------
// Diagnóstico, para cuando el aviso no llega
// ---------------------------------------------------------------------------

export type EstadoServidor =
  | { tipo: 'arriba' }
  /** Responde, pero con otra clave: la app y el servidor no son pareja. */
  | { tipo: 'otra_clave'; delServidor: string }
  | { tipo: 'no_responde' };

/**
 * Pregunta al servidor si está arriba y si su clave pública es la misma que
 * trae la app. Es el primer lugar que hay que mirar cuando un aviso no llega:
 * si las claves no coinciden, Apple rechaza cada aviso y no hay forma de
 * notarlo desde el teléfono.
 */
export async function estadoDelServidorDeAvisos(): Promise<EstadoServidor> {
  try {
    const respuesta = await fetch(`${SERVIDOR_AVISOS}/salud`, {
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    });
    if (!respuesta.ok) return { tipo: 'no_responde' };
    const datos = (await respuesta.json()) as { ok?: boolean; clave?: string };
    if (datos.ok !== true || typeof datos.clave !== 'string') return { tipo: 'no_responde' };
    if (datos.clave !== CLAVE_PUBLICA_AVISOS) {
      return { tipo: 'otra_clave', delServidor: datos.clave };
    }
    return { tipo: 'arriba' };
  } catch {
    return { tipo: 'no_responde' };
  }
}
